<template>
  <div class="customer-recycle-container">
    <div class="page-header">
      <div class="page-title-block">
        <h2>客户回收站</h2>
        <p class="page-subtitle">查看并恢复已删除的客户信息</p>
      </div>
      <div class="header-actions">
        <el-button
          v-if="!isMobile"
          :disabled="multipleSelection.length === 0"
          type="success"
          @click="handleBatchRestore"
        >
          <i class="el-icon-refresh-left"></i> 批量恢复
        </el-button>
        <el-button
          v-if="!isMobile"
          :disabled="multipleSelection.length === 0"
          type="danger"
          @click="handleBatchDelete"
        >
          <i class="el-icon-delete"></i> 批量彻底删除
        </el-button>
        <el-button @click="goBack">
          <i class="el-icon-back"></i> 返回客户列表
        </el-button>
      </div>
    </div>

    <el-card class="search-card">
      <div class="search-form">
        <el-form :inline="true" :model="searchForm" class="search-inline-form">
          <el-form-item label="客户名称">
            <el-input v-model="searchForm.name" placeholder="请输入客户名称" clearable @input="handleSearch"></el-input>
          </el-form-item>
          <el-form-item label="手机号">
            <el-input v-model="searchForm.phone" placeholder="请输入手机号" clearable @input="handleSearch"></el-input>
          </el-form-item>
          <el-form-item label="地址">
            <el-input v-model="searchForm.address" placeholder="请输入地址" clearable @input="handleSearch"></el-input>
          </el-form-item>
          <el-form-item label="删除时间">
            <el-date-picker
              v-model="searchForm.deletedRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              value-format="yyyy-MM-dd"
              @change="handleSearch"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="handleSearch">
              <i class="el-icon-search"></i> 搜索
            </el-button>
            <el-button @click="handleReset">
              <i class="el-icon-refresh"></i> 重置
            </el-button>
          </el-form-item>
        </el-form>
      </div>
    </el-card>

    <!-- 移动端卡片视图 -->
    <div v-if="isMobile" class="customer-list-mobile">
      <div
        v-for="customer in filteredCustomers"
        :key="customer.id"
        class="customer-card"
      >
        <div class="customer-info">
          <h3>{{ customer.name }}</h3>
          <p><i class="el-icon-phone"></i> {{ customer.phone }}</p>
          <p><i class="el-icon-location-outline"></i> {{ customer.address }}</p>
          <p><i class="el-icon-user"></i> 管理者：{{ customer.manager && customer.manager.name ? customer.manager.name : '无' }}</p>
          <p v-if="customer.deletedAt"><i class="el-icon-time"></i> 删除时间：{{ formatDeletedAt(customer.deletedAt) }}</p>
        </div>
        <div class="customer-actions">
          <el-checkbox
            v-model="selectedIds"
            :label="customer.id"
            class="card-checkbox"
          >
            选择
          </el-checkbox>
          <el-button type="primary" size="mini" @click="handleViewCustomer(customer)">
            查看
          </el-button>
          <el-button type="success" size="mini" @click="handleRestoreCustomer(customer)">
            恢复
          </el-button>
          <el-button type="danger" size="mini" @click="handleDeleteCustomer(customer)">
            彻底删除
          </el-button>
        </div>
        <div
          class="mobile-batch-bar"
          v-if="isMobile && selectedIds.length > 0"
        >
          <el-button size="mini" type="success" @click="handleBatchRestore">
            批量恢复 ({{ selectedIds.length }})
          </el-button>
          <el-button size="mini" type="danger" @click="handleBatchDelete">
            批量彻底删除 ({{ selectedIds.length }})
          </el-button>
        </div>
      </div>
    </div>

    <!-- 桌面端表格视图 -->
    <el-card v-else>
      <el-table
        :data="filteredCustomers"
        style="width: 100%"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="55"></el-table-column>
        <el-table-column prop="id" label="ID" width="80"></el-table-column>
        <el-table-column prop="name" label="客户名称" min-width="150"></el-table-column>
        <el-table-column prop="phone" label="联系电话" min-width="150"></el-table-column>
        <el-table-column prop="address" label="地址" min-width="200"></el-table-column>
        <el-table-column prop="manager.name" label="管理者" min-width="120"></el-table-column>
        <el-table-column
          prop="deletedAt"
          label="删除时间"
          min-width="180"
        >
          <template slot-scope="scope">
            {{ formatDeletedAt(scope.row.deletedAt) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" min-width="260" fixed="right">
          <template slot-scope="scope">
            <el-button type="primary" size="small" @click="handleViewCustomer(scope.row)">
              查看
            </el-button>
            <el-button type="success" size="small" @click="handleRestoreCustomer(scope.row)">
              恢复
            </el-button>
            <el-button type="danger" size="small" @click="handleDeleteCustomer(scope.row)">
              彻底删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
import request from '../utils/request'
import { getUserInfo } from '../utils/auth'
import { filterCustomersByDataPermission } from '../utils/dataPermission'

export default {
  name: 'CustomerRecycleBin',
  data() {
    return {
      customers: [],
      filteredCustomers: [],
      isMobile: false,
      currentUser: null,
      searchForm: {
        name: '',
        phone: '',
        address: '',
        deletedRange: []
      },
      multipleSelection: [],
      selectedIds: []
    }
  },
  mounted() {
    this.checkScreenSize()
    window.addEventListener('resize', this.checkScreenSize)
    this.currentUser = getUserInfo()
    this.loadDeletedCustomers()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScreenSize)
  },
  methods: {
    checkScreenSize() {
      this.isMobile = window.innerWidth < 768
    },
    goBack() {
      this.$router.push('/customers')
    },
    // 加载已删除客户
    loadDeletedCustomers() {
      request.get('/customers/deleted')
        .then(response => {
          this.customers = response.data || response
          // 与客户列表保持一致的权限过滤：admin 查看全部，其他仅本部门
          if (this.currentUser && this.currentUser.username === 'admin') {
            this.filteredCustomers = this.sortByDeletedAtDesc(this.customers)
          } else {
            const list = this.customers.filter(customer => {
              return customer.manager &&
                     customer.manager.department &&
                     this.currentUser &&
                     this.currentUser.department &&
                     customer.manager.department.id === this.currentUser.department.id
            })
            this.filteredCustomers = this.sortByDeletedAtDesc(list)
          }
        })
        .catch(error => {
          console.error('加载回收站客户失败:', error)
          this.$message.error('加载回收站客户失败')
        })
    },
    // 搜索
    handleSearch() {
      let filtered = [...this.customers]
      if (this.searchForm.name) {
        filtered = filtered.filter(customer => customer.name && customer.name.includes(this.searchForm.name))
      }
      if (this.searchForm.phone) {
        filtered = filtered.filter(customer => customer.phone && customer.phone.includes(this.searchForm.phone))
      }
      if (this.searchForm.address) {
        filtered = filtered.filter(customer => customer.address && customer.address.includes(this.searchForm.address))
      }

      // 按删除时间范围过滤（仅按日期比较）
      if (this.searchForm.deletedRange && this.searchForm.deletedRange.length === 2) {
        const [start, end] = this.searchForm.deletedRange
        filtered = filtered.filter(customer => {
          if (!customer.deletedAt) return false
          const d = customer.deletedAt.substring(0, 10)
          return d >= start && d <= end
        })
      }

      const list = filterCustomersByDataPermission(filtered, this.currentUser)
      this.filteredCustomers = this.sortByDeletedAtDesc(list)
    },
    handleReset() {
      this.searchForm = {
        name: '',
        phone: '',
        address: '',
        deletedRange: []
      }
      this.loadDeletedCustomers()
    },
    sortByDeletedAtDesc(list) {
      return [...list].sort((a, b) => {
        if (!a.deletedAt && !b.deletedAt) return 0
        if (!a.deletedAt) return 1
        if (!b.deletedAt) return -1
        return new Date(b.deletedAt) - new Date(a.deletedAt)
      })
    },
    formatDeletedAt(value) {
      if (!value) return '-'
      try {
        const d = new Date(value)
        if (isNaN(d.getTime())) {
          return value
        }
        const y = d.getFullYear()
        const m = String(d.getMonth() + 1).padStart(2, '0')
        const day = String(d.getDate()).padStart(2, '0')
        const hh = String(d.getHours()).padStart(2, '0')
        const mm = String(d.getMinutes()).padStart(2, '0')
        return `${y}-${m}-${day} ${hh}:${mm}`
      } catch (e) {
        return value
      }
    },
    handleSelectionChange(selection) {
      this.multipleSelection = selection
      this.selectedIds = selection.map(item => item.id)
    },
    // 查看客户
    handleViewCustomer(customer) {
      this.$router.push(`/customers/${customer.id}`)
    },
    // 恢复客户
    handleRestoreCustomer(customer) {
      this.$confirm(`确定要恢复客户 "${customer.name}" 吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        request.put(`/customers/${customer.id}/restore`)
          .then(() => {
            this.$message.success('恢复客户成功')
            this.loadDeletedCustomers()
          })
          .catch(error => {
            console.error('恢复客户失败:', error)
            this.$message.error('恢复客户失败')
          })
      }).catch(() => {})
    },
    // 彻底删除单个客户
    handleDeleteCustomer(customer) {
      this.$confirm(`此操作将永久删除客户 "${customer.name}" 及其相关回访记录，是否继续？`, '警告', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        request.delete(`/customers/${customer.id}/permanent`)
          .then(() => {
            this.$message.success('彻底删除客户成功')
            this.loadDeletedCustomers()
          })
          .catch(error => {
            console.error('彻底删除客户失败:', error)
            this.$message.error('彻底删除客户失败')
          })
      }).catch(() => {})
    },
    // 批量恢复
    handleBatchRestore() {
      const ids = this.selectedIds
      if (!ids || ids.length === 0) {
        this.$message.warning('请先选择要恢复的客户')
        return
      }
      this.$confirm(`确定要批量恢复选中的 ${ids.length} 个客户吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        request.post('/customers/recycle-bin/restore', ids)
          .then(() => {
            this.$message.success('批量恢复客户成功')
            this.selectedIds = []
            this.multipleSelection = []
            this.loadDeletedCustomers()
          })
          .catch(error => {
            console.error('批量恢复客户失败:', error)
            this.$message.error('批量恢复客户失败')
          })
      }).catch(() => {})
    },
    // 批量彻底删除
    handleBatchDelete() {
      const ids = this.selectedIds
      if (!ids || ids.length === 0) {
        this.$message.warning('请先选择要彻底删除的客户')
        return
      }
      this.$confirm(`此操作将永久删除选中的 ${ids.length} 个客户及其回访记录，是否继续？`, '警告', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        request.post('/customers/recycle-bin/delete', ids)
          .then(() => {
            this.$message.success('批量彻底删除客户成功')
            this.selectedIds = []
            this.multipleSelection = []
            this.loadDeletedCustomers()
          })
          .catch(error => {
            console.error('批量彻底删除客户失败:', error)
            this.$message.error('批量彻底删除客户失败')
          })
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.customer-recycle-container {
  width: 100%;
  padding: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
  padding: 14px 4px 6px;
}

.page-title-block h2 {
  margin: 0;
  font-size: 22px;
  color: #0f8f59;
}

.page-subtitle {
  margin-top: 4px;
  font-size: 12px;
  color: #80848f;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.search-card {
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(23, 182, 118, 0.08);
  border: none;
}

.customer-list-mobile {
  display: grid;
  gap: 15px;
}

.customer-card {
  background-color: #fff;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #ebeef5;
}

.customer-info h3 {
  margin: 0 0 10px 0;
  color: #303133;
  font-size: 16px;
  font-weight: bold;
}

.customer-info p {
  margin: 5px 0;
  color: #606266;
  font-size: 14px;
}

.customer-info i {
  margin-right: 5px;
  color: #409eff;
}

.customer-actions {
  margin-top: 15px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.customer-actions .el-button {
  margin: 0;
}

.card-checkbox {
  margin-right: 8px;
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: stretch;
    gap: 15px;
  }

  .customer-recycle-container {
    padding: 0 4px;
  }

  .customer-card {
    padding: 12px;
  }

  .customer-info h3 {
    font-size: 15px;
  }

  .customer-info p {
    font-size: 13px;
  }

  .customer-actions {
    justify-content: center;
  }
}
</style>

